import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { DatacloudTableRelationship } from '../../models/appcloud/datacloud-table-relationship.model';

@Injectable({
    providedIn: 'root',
})
export class DatacloudTableRelationshipService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private DATACLOUD_TABLE_RELATIONSHIPTS_URL: string =
        environment.publicApiHost + '/appcloud/datacloud-table-relationships';

    getById(id: number, additionalHeaders: any = {}): Observable<DatacloudTableRelationship> {
        this.#spinnerService.addProcess(
            'DatacloudTableRelationshipService.getById.' + id,
            'Loading your table relationship...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .get<DatacloudTableRelationship>(`${this.DATACLOUD_TABLE_RELATIONSHIPTS_URL}/${id}`, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while retrieving table relationship data from Relationship One. Please try again later.'
                    );
                    return of<DatacloudTableRelationship>();
                }),
                finalize(() => this.#spinnerService.completeProcess('DatacloudTableRelationshipService.getById.' + id))
            );
    }

    findAll(eloquainstallId: number, additionalHeaders: any = {}): Observable<DatacloudTableRelationship[]> {
        this.#spinnerService.addProcess(
            'DatacloudTableRelationshipService.findAll.' + eloquainstallId,
            'Loading your table relationships...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', eloquainstallId.toString()),
        };

        return this.#http.get<DatacloudTableRelationship[]>(this.DATACLOUD_TABLE_RELATIONSHIPTS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving table relationship data from Relationship One. Please try again later.'
                );
                return of<DatacloudTableRelationship[]>();
            }),
            finalize(() =>
                this.#spinnerService.completeProcess('DatacloudTableRelationshipService.findAll.' + eloquainstallId)
            )
        );
    }

    create(dbTable: DatacloudTableRelationship, additionalHeaders: any = {}): Observable<DatacloudTableRelationship> {
        this.#spinnerService.addProcess(
            'DatacloudTableRelationshipService.create',
            'Creating your datacloud table relationship...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .post<DatacloudTableRelationship>(this.DATACLOUD_TABLE_RELATIONSHIPTS_URL, dbTable, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while saving your table relationship. Please try again later.'
                    );
                    return of<DatacloudTableRelationship>();
                }),
                finalize(() => {
                    this.#spinnerService.completeProcess('DatacloudTableRelationshipService.create');
                })
            );
    }

    update(id: number, datacloudTable: DatacloudTableRelationship, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess(
            'DatacloudTableRelationshipService.update.' + id,
            'Updaing your table relationship...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http
            .patch<DatacloudTableRelationship>(
                this.DATACLOUD_TABLE_RELATIONSHIPTS_URL + '/' + id,
                datacloudTable,
                httpOptions
            )
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating the table relationship. Please try again later.'
                    );
                    return of<DatacloudTableRelationship>();
                }),
                finalize(() => this.#spinnerService.completeProcess('DatacloudTableRelationshipService.update.' + id))
            );
    }

    delete(id: number, additionalHeaders: any = {}): Observable<any> {
        this.#spinnerService.addProcess(
            'DatacloudTableRelationshipService.delete.' + id,
            'Deleting your table relationship...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.delete<Observable<any>>(`${this.DATACLOUD_TABLE_RELATIONSHIPTS_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while deleting the table relationship. Please try again later.'
                );
                return of<Observable<any>>();
            }),
            finalize(() => this.#spinnerService.completeProcess('DatacloudTableRelationshipService.delete.' + id))
        );
    }
}
